import React, { FC, useState, useEffect, useMemo } from 'react'
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/outline'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Heading, Button } from '../System'
import { useRegisterHandler } from '../Auth'
import { ReactComponent as WaveSvg } from './wave.svg'
import { MainInfoStep, SubscriptionStep, PaymentStep } from './Steps'
import { StyledRegisterView } from '.'
import { Marketing } from '../Auth/Marketing'
import { RegistrationLoader } from './RegistrationLoader'
import { FormMessage } from '../System/Form'
import { TRIAL, CHALLENGE } from '../utils/stripePrices'
import usePlanQuery from '../utils/usePlanQuery'
import { AppState } from '../../redux/root-reducer'
import { ChallengeState } from '../../redux/challenge/types'
import ROUTES from '../utils/routes'

const RegisterView: FC = () => {
  // Redirect to platform signup page
  useEffect(() => {
    const searchParams = window.location.search
    window.location.href = `https://app.socialcurator.com/signup${searchParams}`
  }, [])

  const {
    form,
    validation,
    loadingMessage,
    formError,
    actions: { handleRegister }
  } = useRegisterHandler()
  const history = useHistory()
  const { matchedSubscription } = usePlanQuery()
  const { currentChallenge, loading: challengeLoading } = useSelector<AppState, ChallengeState>(
    (state) => state.challenge
  )
  const showChallengeCopy = matchedSubscription?.id === CHALLENGE && currentChallenge

  const STEPS = ['Main Information', 'Subscription', 'Payment', 'Complete Registration']
  const [loading, setLoading] = useState(false)
  const [currStep, setCurrStep] = useState(0)
  const { setValue } = form

  const nextStep = async () => {
    const subscriptionId = form.values?.subscription?.id
    const isTrial = subscriptionId === TRIAL
    const isChallenge = subscriptionId === CHALLENGE
    const isValid = await form.trigger()

    if (!isValid) {
      return
    }

    if (isTrial || isChallenge) {
      setLoading(true)
      handleRegister()
      return
    }

    // last step is decorative, so we want to submit
    // the form on the second-last step
    if (currStep === STEPS.length - 2) {
      setLoading(true)
      handleRegister()
      return
    }

    // go to payment step if we already have subscription data
    if (form.values?.subscription?.id) {
      setCurrStep(2)
      return
    }

    setCurrStep(currStep + 1)
  }

  const nextSectionName = useMemo(() => {
    const subscriptionId = form.values?.subscription?.id
    const subscriptionAlreadyDefined = !!subscriptionId
    const isTrial = subscriptionId === TRIAL
    const isChallenge = subscriptionId === CHALLENGE

    if (isTrial || isChallenge) {
      return STEPS[3]
    }

    if (subscriptionAlreadyDefined && currStep !== 2) {
      return STEPS[2]
    }

    return STEPS[currStep + 1]
  }, [form.values?.subscription?.id, currStep])

  // redirect in the case where user is not allowed to join a challenge
  useEffect(() => {
    // eject if if plan URL query is not `Challenge` or challenge data is still loading
    if (matchedSubscription?.id !== CHALLENGE || challengeLoading) {
      return
    }

    const joinBy = new Date(currentChallenge?.joinBy || 0).valueOf()

    // redirect if no challenge is running or if user missed join by date
    if (!currentChallenge || joinBy < new Date().valueOf()) {
      window.location.replace(ROUTES.external.challengeWaitlistUrl)
    }
  }, [matchedSubscription])

  // update sign up state to add chosen subscription if a plan is found in the URL
  useEffect(() => {
    setValue('subscription', matchedSubscription)
  }, [matchedSubscription])

  return (
    <StyledRegisterView currStep={currStep}>
      <Marketing />
      <div id="form" className="auth-information">
        <div className="wave">
          <WaveSvg />
        </div>
        <div className="back-btn">
          {currStep > 0 && (
            <Button theme="ghost" isIcon onClick={() => setCurrStep(currStep - 1)}>
              <ChevronLeftIcon />
            </Button>
          )}
        </div>
        <div className="auth-form">
          {!showChallengeCopy && (
            <Heading>
              Get Started with
              <br />
              Social Curator
            </Heading>
          )}

          {showChallengeCopy && (
            <Heading>
              Start the Free
              <br />
              Challenge Today
            </Heading>
          )}

          <div className="breadcrumbs">
            <b>{STEPS[currStep]}</b>
            {currStep !== STEPS.length - 1 && (
              <>
                <ChevronRightIcon />
                {nextSectionName}
              </>
            )}
          </div>

          {formError && <FormMessage message={formError} type="error" />}

          {currStep === 0 && (
            <MainInfoStep form={form} validation={validation} nextStep={() => nextStep()} />
          )}
          {currStep === 1 && <SubscriptionStep form={form} nextStep={() => nextStep()} />}
          {currStep === 2 && (
            <PaymentStep
              form={form}
              changePlan={() => setCurrStep(1)}
              nextStep={() => nextStep()}
            />
          )}
          {loading && !formError && <RegistrationLoader currentProgress={loadingMessage} />}
        </div>
      </div>
    </StyledRegisterView>
  )
}

export default RegisterView
